import { z } from 'zod'
import { oc } from '@orpc/contract'

/** Statuses Zocdoc reschedules from. */
export const ZOCDOC_RESCHEDULABLE_STATUSES = [
  'pending_booking',
  'confirmed',
  'pending_reschedule',
  'rescheduled',
] as const

/**
 * What the mapped practitioner's beHuman calendar holds at a candidate instant.
 *
 * Computed with the **same lookup the write performs**, so an option the picker offers is one the
 * reschedule can actually take. Four answers rather than a boolean, because the operator's next
 * move differs for each — and only `free` and `no-slot` are reschedulable at all.
 */
const BEHUMAN_SLOT_STATES = [
  /** A `free` Slot at this instant on the practitioner's schedule. Move straight into it. */
  'free',
  /**
   * A Slot exists but is taken — an appointment already occupies this time.
   *
   * **Blocks the option.** Creating a second slot on top would double-book the practitioner, so
   * this is never offered as a target and never repairable by the create-slot checkbox.
   */
  'claimed',
  /** No Slot at all. Reschedulable only with `createSlot`, which publishes availability. */
  'no-slot',
  /**
   * The practitioner has no `Schedule`, so no Slot can exist or be created — a `Slot` needs a
   * schedule reference. Not repairable here; the practitioner has to be set up on the Schedule
   * page first.
   */
  'no-schedule',
] as const

/**
 * The reschedule options for one Zocdoc appointment: what Zocdoc will accept, annotated with what
 * beHuman holds at each of those instants.
 *
 * **One endpoint rather than two calls from the browser.** The conflict answer has to come from
 * the same schedule-scoped `Slot` lookup the write uses (`findFreeSlot`), and re-deriving that in
 * the browser would let the picker offer a slot the write then refuses. Reading it here means the
 * option shown and the option taken are decided by one piece of code.
 *
 * A read: nothing is sent to Zocdoc beyond its own availability GETs, and nothing is written.
 */
export const zocdocRescheduleOptions = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/reschedule-options' })
  .input(
    z.object({
      zocdocAppointmentId: z.string().min(1),
      /** Inclusive `YYYY-MM-DD` window. One Zocdoc request per day, as the availability read is. */
      fromDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      toDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }),
  )
  .output(
    z.object({
      /** The appointment as it stands, so the comparison has a left-hand side. */
      current: z.object({
        /** Offset-bearing, exactly as Zocdoc sent it. */
        startAt: z.string().optional(),
        status: z.string().optional(),
        timeZone: z.string().optional(),
        locationId: z.string().optional(),
        visitReasonId: z.string().optional(),
        npi: z.string().optional(),
        practitionerId: z.string().optional(),
        practitionerName: z.string().optional(),
        /** The bound beHuman visit, when this booking was imported. */
        behumanAppointmentId: z.string().optional(),
        behumanEncounterId: z.string().optional(),
        /** Minutes the current visit occupies — the length a created slot would copy. */
        durationMinutes: z.number().optional(),
      }),
      /**
       * Set when the appointment cannot be rescheduled at all, so the picker is never shown for a
       * case the write would refuse. The days list is then empty.
       */
      refusal: z
        .object({
          reason: z.enum([
            'not-found',
            'status-not-reschedulable',
            'provider-unmapped',
            /** The linked practitioner's chart carries no `us-npi` to query Zocdoc by. */
            'practitioner-has-no-npi',
            /** Zocdoc did not answer for that provider — nothing for an operator to re-map. */
            'zocdoc-unknown',
          ]),
          detail: z.string(),
        })
        .optional(),
      /** One entry per day in the window, in date order, including days with no slots. */
      days: z.array(
        z.object({
          date: z.string(),
          slots: z.array(
            z.object({
              /** Local wall clock `HH:mm`, the shape the write takes. Never a display string. */
              time: z.string(),
              /** The full local date-time Zocdoc published, for the payload preview. */
              startTime: z.string().optional(),
              timeZone: z.string().optional(),
              locationId: z.string().optional(),
              locationName: z.string().optional(),
              /** The UTC instant this resolves to — what beHuman stores and matches on. */
              utcInstant: z.string().optional(),
              behuman: z.object({
                state: z.enum(BEHUMAN_SLOT_STATES),
                slotId: z.string().optional(),
                /** Why a `claimed` slot is taken, when the appointment on it could be read. */
                detail: z.string().optional(),
              }),
            }),
          ),
          /** Set when that day's read failed; its slots are unknown, not empty. */
          error: z.string().optional(),
        }),
      ),
      missingEnv: z.array(z.string()).optional(),
    }),
  )

/**
 * Moves a booking to a new start **on Zocdoc**, and moves the bound beHuman visit with it
 * (`POST /v1/appointments/reschedule`).
 *
 * Its own route rather than a branch of an update endpoint, for the reason set out in
 * `zocdoc-cancel-appointment.contract.ts`: Zocdoc's appointment writes take disjoint payloads and
 * are legal from different statuses.
 *
 * **The one action on this screen that writes FHIR.** Confirm, cancel and the status writes are
 * Zocdoc-only; a reschedule is not, because leaving the beHuman visit at the old time would leave
 * the practitioner's calendar actively wrong rather than merely stale.
 *
 * Order of operations, and it matters — Zocdoc offers no idempotency key and no undo:
 *
 * 1. **check beHuman first** and refuse before anything is sent, so the common failure ("nothing
 *    free at that time") costs no upstream write;
 * 2. create the Slot, if `createSlot` was ticked;
 * 3. send Zocdoc the reschedule;
 * 4. move the beHuman visit and release the old Slot;
 * 5. settle the booking to `confirmed` on Zocdoc — **only if step 4 moved it**, so the status
 *    never claims a settled visit while our calendar still holds the old time.
 *
 * A failure at step 4 after step 3 succeeded is **reported, not rolled back** — `behumanMoved`
 * is false and `behumanFailure` says what to fix by hand. Rolling Zocdoc back would be a second
 * unretryable write against a row that just changed. A failure at step 5 is reported the same
 * way (`zocdocConfirmFailure`) and is the mildest of the three: both calendars hold the new time
 * and Zocdoc calls `rescheduled` settled, so only the status is short.
 */
export const zocdocRescheduleAppointment = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/reschedule' })
  .input(
    z
      .object({
        zocdocAppointmentId: z.string().min(1),
        /**
         * The target slot's local date and wall clock, exactly as the options read returned them.
         *
         * Optional only because `startAt` is the other way in — one of the two forms is required,
         * and the refinement below says so.
         */
        day: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional(),
        time: z
          .string()
          .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
          .optional(),
        /**
         * The target instant, absolute — the other way to name the new time, and the one the
         * practitioner's Schedule page uses.
         *
         * It picks a slot off the beHuman calendar, so it knows the *moment* and has no business
         * guessing which wall clock that is in Zocdoc's location zone. Given, it wins: the handler
         * converts it into that zone itself. The Zocdoc console keeps sending `day`/`time`, which
         * is what its options read hands back.
         */
        startAt: z.string().datetime({ offset: true }).optional(),
        /** IANA zone the wall clock is in. Omitted = the zone Zocdoc reports for the location. */
        timeZone: z.string().optional(),
        /** The target slot's bare `lo_…`, when it names one. */
        locationId: z.string().optional(),
        /**
         * Publish a `free` Slot at the new instant before moving, when the practitioner has none.
         *
         * Off by default, and deliberately explicit: this commits a practitioner to a time they
         * never offered. Ignored — and refused — when a Slot already exists and is taken, because
         * a second slot over a booked one double-books the doctor.
         */
        createSlot: z.boolean().optional(),
      })
      // One of the two ways to name the new time, never neither.
      .refine((value) => Boolean(value.startAt) || Boolean(value.day && value.time), {
        message: 'Provide either startAt, or both day and time',
      }),
  )
  .output(
    z.object({
      status: z.enum(['rescheduled', 'refused']),
      refusal: z
        .object({
          reason: z.enum([
            'not-found',
            'status-not-reschedulable',
            'provider-unmapped',
            /** The linked practitioner's chart carries no `us-npi` to query Zocdoc by. */
            'practitioner-has-no-npi',
            /** Zocdoc did not answer for that provider — nothing for an operator to re-map. */
            'zocdoc-unknown',
            'no-schedule',
            /** A slot exists at the new time and is taken. Never repairable by `createSlot`. */
            'slot-claimed',
            /** No slot, and `createSlot` was not ticked. */
            'no-slot',
            'zocdoc-rejected',
          ]),
          detail: z.string(),
        })
        .optional(),
      /** Offset-bearing instants, so the caller can show exactly what moved where. */
      previousStartAt: z.string().optional(),
      newStartAt: z.string().optional(),
      previousStatus: z.string().optional(),
      observedStatus: z.string().optional(),
      /** The body Zocdoc was sent, so a rejected payload is diagnosable rather than opaque. */
      sentStartTime: z.string().optional(),
      /** A Slot was published because `createSlot` was ticked. */
      slotCreated: z.boolean().optional(),
      /**
       * Whether the bound beHuman visit was moved.
       *
       * False with `status: 'rescheduled'` is the split state worth reading out loud: Zocdoc moved,
       * beHuman did not, and the practitioner's calendar is now wrong until someone fixes it.
       */
      behumanMoved: z.boolean().optional(),
      behumanAppointmentId: z.string().optional(),
      /** Why the beHuman half did not move, when Zocdoc's half did. */
      behumanFailure: z.string().optional(),
      /**
       * Whether the booking was settled from `rescheduled` to `confirmed` on Zocdoc.
       *
       * Absent when there was nothing to settle — the beHuman half did not move, so step 5 was
       * deliberately skipped. Prefer this over `observedStatus` for "did it land": Zocdoc's read
       * lags its own writes and reports the pre-write status for a few seconds afterwards.
       */
      zocdocConfirmed: z.boolean().optional(),
      /** Why the booking is still `rescheduled` rather than `confirmed`. Not a failed move. */
      zocdocConfirmFailure: z.string().optional(),
      requestId: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )
